import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, Send, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { trackClick } from "@/lib/api";
import { useContact } from "@/lib/contact";

const EASE = [0.22, 1, 0.36, 1];

const DEFAULT_MESSAGE = "Hi Work Island, I'd like to know more about your workspaces!";
const DEMO_MESSAGE = "Hi Work Island, I would like to book a free demo.";
const PLANS_MESSAGE = "Hi Work Island, I would like to know more about your monthly plans.";

const QUICK_REPLIES = [
    { id: "demo", label: "Book a Free Demo", msg: "Hi Work Island, I would like to book a free demo of the workspace." },
    { id: "options", label: "View Workspace Options", msg: "Hi Work Island, I would like to know more about your coworking, private cabin and premium cabin options." },
    { id: "plans", label: "Check Monthly Plans", msg: "Hi Work Island, I would like to know more about your monthly coworking plans." },
    { id: "cabin", label: "Private Cabin Enquiry", msg: "Hi Work Island, I am interested in a private cabin. Please share the details." },
    { id: "meeting", label: "Meeting Room Enquiry", msg: "Hi Work Island, I am interested in your meeting room. Please share the details." },
];

export default function WhatsAppWidget() {
    const contact = useContact();
    const [open, setOpen] = useState(false);
    const [showTip, setShowTip] = useState(false);
    const [customMsg, setCustomMsg] = useState("");
    const [contextMsg, setContextMsg] = useState(DEFAULT_MESSAGE);
    const timerRef = useRef(null);

    // One subtle attention cue, shown once after a short delay
    useEffect(() => {
        timerRef.current = setTimeout(() => setShowTip(true), 5000);
        return () => clearTimeout(timerRef.current);
    }, []);

    useEffect(() => {
        if (!showTip) return;
        const hide = setTimeout(() => setShowTip(false), 5500);
        return () => clearTimeout(hide);
    }, [showTip]);

    // Contextual default message near conversion sections
    useEffect(() => {
        const demo = document.querySelector("#book-demo");
        const pricing = document.querySelector("#pricing");
        if (!demo && !pricing) return;
        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting) return;
                    setContextMsg(entry.target.id === "book-demo" ? DEMO_MESSAGE : PLANS_MESSAGE);
                });
            },
            { rootMargin: "-35% 0px -35% 0px" },
        );
        if (demo) io.observe(demo);
        if (pricing) io.observe(pricing);
        return () => io.disconnect();
    }, []);

    // --- tracking-ready handlers (wire GA / Meta Pixel events here later) ---
    const handleWidgetToggle = () => {
        setShowTip(false);
        setOpen((v) => {
            if (!v) trackClick("whatsapp", "whatsapp-widget");
            return !v;
        });
    };

    const handleQuickReply = (reply) => {
        trackClick("whatsapp", "whatsapp-widget");
        window.open(contact.waLink(reply.msg), "_blank", "noopener,noreferrer");
    };

    const handleContinue = () => {
        trackClick("whatsapp", "whatsapp-widget");
        window.open(contact.waLink(contextMsg), "_blank", "noopener,noreferrer");
    };

    const handleCustomStart = (e) => {
        e.preventDefault();
        const msg = customMsg.trim() ? `Hi Work Island, ${customMsg.trim()}` : contextMsg;
        trackClick("whatsapp", "whatsapp-widget");
        window.open(contact.waLink(msg), "_blank", "noopener,noreferrer");
        setCustomMsg("");
    };

    return (
        <div className="fixed bottom-6 right-6 z-[65] hidden md:block" data-testid="whatsapp-widget">
            {/* attention tooltip — appears once */}
            <AnimatePresence>
                {showTip && !open && (
                    <motion.div
                        initial={{ opacity: 0, y: 8, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 6, scale: 0.97 }}
                        transition={{ duration: 0.45, ease: EASE }}
                        className="absolute bottom-full right-0 mb-3 w-max max-w-[240px] rounded-2xl rounded-br-md border border-slate-200/80 bg-white px-4 py-3 text-[12.5px] font-semibold text-ink shadow-[0_16px_40px_-18px_rgba(22,50,79,0.4)]"
                        data-testid="whatsapp-tooltip"
                    >
                        Need help choosing a workspace?
                    </motion.div>
                )}
            </AnimatePresence>

            {/* chat panel */}
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: 14, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.98 }}
                        transition={{ duration: 0.4, ease: EASE }}
                        role="dialog"
                        aria-label="Work Island WhatsApp chat"
                        data-testid="whatsapp-panel"
                        className="absolute bottom-full right-0 mb-3 w-[360px] max-w-[calc(100vw-24px)] overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-[0_36px_80px_-30px_rgba(22,50,79,0.45)]"
                    >
                        {/* header */}
                        <div className="flex items-center gap-3 bg-gradient-to-r from-[#152B41] to-[#0E2233] px-5 py-4">
                            <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-purple via-brand-blue to-brand-teal text-white">
                                <MessageCircle size={18} strokeWidth={2.3} />
                            </span>
                            <div className="flex-1">
                                <p className="text-sm font-bold text-white">Work Island</p>
                                <p className="mt-0.5 flex items-center gap-1.5 text-[11px] font-semibold text-slate-400">
                                    <span className="h-1.5 w-1.5 rounded-full bg-brand-teal" aria-hidden="true" />
                                    Typically replies quickly
                                </p>
                            </div>
                            <button
                                data-testid="whatsapp-panel-close"
                                onClick={() => setOpen(false)}
                                aria-label="Close chat"
                                className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-white/10 hover:text-white"
                            >
                                <X size={16} />
                            </button>
                        </div>

                        {/* welcome + quick replies */}
                        <div className="bg-canvas px-5 py-5">
                            <div className="max-w-[85%] rounded-2xl rounded-tl-md border border-slate-200/70 bg-white px-4 py-3 shadow-sm">
                                <p className="text-[13px] font-medium leading-relaxed text-ink/80">
                                    Hi 👋
                                    <br />
                                    Welcome to Work Island.
                                    <br />
                                    How can we help you today?
                                </p>
                            </div>

                            <div className="mt-4 flex flex-wrap gap-2">
                                {QUICK_REPLIES.map((reply) => (
                                    <button
                                        key={reply.id}
                                        data-testid={`whatsapp-quick-${reply.id}`}
                                        onClick={() => handleQuickReply(reply)}
                                        className="rounded-full border border-brand-blue/25 bg-white px-3.5 py-2 text-[12px] font-bold text-brand-blue transition-all duration-300 hover:-translate-y-0.5 hover:border-brand-blue/50 hover:shadow-[0_8px_20px_-10px_rgba(43,92,171,0.5)]"
                                    >
                                        {reply.label}
                                    </button>
                                ))}
                            </div>

                            {/* optional custom message */}
                            <form onSubmit={handleCustomStart} className="mt-4 flex items-center gap-2">
                                <input
                                    data-testid="whatsapp-custom-input"
                                    value={customMsg}
                                    onChange={(e) => setCustomMsg(e.target.value)}
                                    placeholder="Type your message…"
                                    aria-label="Type your message"
                                    className="w-full rounded-full border border-slate-200 bg-white px-4 py-2.5 text-[13px] font-medium text-ink placeholder:text-ink/35 focus:border-brand-blue/50 focus:outline-none focus:ring-4 focus:ring-brand-blue/10"
                                />
                                <button
                                    type="submit"
                                    data-testid="whatsapp-custom-start"
                                    aria-label="Start chat with your message"
                                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-brand-purple via-brand-blue to-brand-teal text-white transition-transform duration-300 hover:-translate-y-0.5 active:scale-95"
                                >
                                    <Send size={15} strokeWidth={2.4} />
                                </button>
                            </form>
                        </div>

                        {/* main CTA */}
                        <div className="border-t border-slate-100 bg-white p-4">
                            <button
                                data-testid="whatsapp-continue-btn"
                                onClick={handleContinue}
                                className="btn-gradient group flex w-full items-center justify-center gap-2 rounded-full px-5 py-3.5 text-sm font-bold text-white"
                            >
                                <MessageCircle size={16} strokeWidth={2.4} />
                                Continue on WhatsApp
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* collapsed pill */}
            <motion.button
                initial={{ opacity: 0, scale: 0.9, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.6, ease: EASE, delay: 0.8 }}
                data-testid="whatsapp-widget-toggle"
                onClick={handleWidgetToggle}
                aria-label="Chat with Work Island on WhatsApp"
                aria-expanded={open}
                className="group ml-auto flex items-center gap-2.5 rounded-full border border-slate-200/80 bg-white py-2.5 pl-2.5 pr-5 shadow-[0_16px_40px_-16px_rgba(22,50,79,0.4)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_22px_48px_-16px_rgba(22,50,79,0.5)]"
            >
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-brand-purple via-brand-blue to-brand-teal text-white">
                    {open ? <X size={16} strokeWidth={2.5} /> : <MessageCircle size={17} strokeWidth={2.3} />}
                </span>
                <span className="text-[13px] font-bold text-ink">Chat with Work Island</span>
            </motion.button>
        </div>
    );
}
